import React, { useEffect, type FC, type ReactNode } from 'react'

import styled from '@emotion/styled'
import { openHelp } from 'Helpers/openHelp'
import { useIntl } from 'react-intl'
import {
  deliverabilityIndexState,
  modalDeliverabilityIndexContactState,
} from '../recoil/state/deliverabilityIndex'

import { DELIVERABILITY_INDEX_TYPE_AUDIT } from 'Constants'
import {
  deliverabilityPendoTrack,
  getDeliverabilityScoreColor,
} from 'Helpers/deliverabilityIndex'
import { evalCondition, notifyParent } from 'mailup-react-helpers'
import {
  BADGE_KIND_PRIMARY,
  Badge,
  LINK_VARIANT_DEFAULT,
  LINK_VARIANT_EXTRALIGHT,
  Link,
  MESSAGE_BOX_VARIANT_HELP,
  MessageBox,
} from 'mailup-web-components'
import {
  useRecoilCallback,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil'
import {
  deliverabilityIndexCallback,
  modalDeliverabilityIndexContactCallback,
} from '../recoil/callbacks/deliverabilityIndex'
import ModalDeliverabilityIndexContact from './modals/ModalDeliverabilityIndexContact'

const StyledMessageBox = styled(MessageBox)`
  a {
    text-decoration: underline;
    color: inherit;
    font-weight: inherit;
  }
  a:hover {
    text-decoration: underline;
  }
`
const StyledBadge = styled(Badge)`
  margin-left: 8px;
`

const BoxDeliverabilityIndex: FC = () => {
  const intl = useIntl()

  const {
    AuditTip,
    ScoreData: { Score },
  } = useRecoilValue(deliverabilityIndexState)

  const isWhiteLabel: boolean = evalCondition('co:WHITELABEL')

  const resetDeliverabilityIndex = useResetRecoilState(deliverabilityIndexState)
  const initDeliverabilityIndex = useRecoilCallback(
    deliverabilityIndexCallback,
    [],
  )
  const openModalDeliverabilityIndexContact = useRecoilCallback(
    modalDeliverabilityIndexContactCallback,
    [],
  )
  const setDeliverabilityIndexContactState = useSetRecoilState(
    modalDeliverabilityIndexContactState,
  )

  const init = (): void => {
    void initDeliverabilityIndex()
  }

  useEffect(() => {
    init()

    return (): void => {
      resetDeliverabilityIndex()
    }
  }, [])

  useEffect(() => {
    deliverabilityPendoTrack(
      'Deliverability index - Warning - Impression',
      DELIVERABILITY_INDEX_TYPE_AUDIT,
      AuditTip,
      Score,
    )
  }, [AuditTip])

  const boxTitle = (
    <>
      {intl.formatMessage(
        { id: 'Load.DeliverabilityIndex.AuditTipTitle' },
        {
          link: (chunks) =>
            !isWhiteLabel ? (
              <Link
                variant={LINK_VARIANT_DEFAULT}
                onClick={() => {
                  notifyParent('openHelp', [openHelp('deliverabilityIndex')])
                }}
              >
                {chunks}
              </Link>
            ) : (
              chunks
            ),
        },
      )}
      <StyledBadge
        kind={BADGE_KIND_PRIMARY}
        type={getDeliverabilityScoreColor(Score)}
        text={`${Math.floor(Score)}/100`}
      />
    </>
  )

  const handleContactModal = (): void => {
    deliverabilityPendoTrack(
      'Deliverability index - Warning - Buy click',
      DELIVERABILITY_INDEX_TYPE_AUDIT,
      AuditTip,
      Score,
    )
    void openModalDeliverabilityIndexContact()
  }

  const getDeliverabilityIndexDescription = (): ReactNode => {
    return intl.formatMessage(
      {
        id: 'Load.DeliverabilityIndex.AuditTipUntrustedForm',
      },
      {
        link: (chunks) => (
          <Link
            variant={LINK_VARIANT_EXTRALIGHT}
            onClick={() => {
              handleContactModal()
            }}
            data-mu-test={`deliverabilityIndex-warning-buyClick-Audit--${AuditTip}`}
          >
            {chunks}
          </Link>
        ),
        linkUntrustedForm: (chunks) =>
          !isWhiteLabel ? (
            <Link
              variant={LINK_VARIANT_EXTRALIGHT}
              onClick={() =>
                notifyParent('openHelp', [openHelp('formProtection')])
              }
              data-mu-test={`deliverabilityIndex-warning-help-Audit--${AuditTip}`}
            >
              {chunks}
            </Link>
          ) : (
            chunks
          ),
      },
    )
  }

  return (
    <>
      {AuditTip && AuditTip !== 'TipsNotFound' && (
        <>
          <ModalDeliverabilityIndexContact />
          <StyledMessageBox
            title={boxTitle}
            variant={MESSAGE_BOX_VARIANT_HELP}
            data-mu-test={`deliverabilityIndex-warning-impression-Audit--${AuditTip}`}
          >
            {getDeliverabilityIndexDescription()}
          </StyledMessageBox>
        </>
      )}
    </>
  )
}

export default BoxDeliverabilityIndex
